import { useCallback, useEffect, useState } from 'react'
import { Pencil, Trash2, X, Eye, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { PageTitle } from '../../components/ui/PageTitle'
import { Pagination } from '../../components/ui/Pagination'
import { StatusPill } from '../../components/ui/StatusPill'
import { api } from '../../lib/api'

type CustomerRow = {
  id: number
  name: string
  mobile: string
  email: string
  gender: string
  status: string
  joined: string
}

function formatDate(value: string) {
  if (!value) return '-'
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
}

export function CustomersPage() {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState<CustomerRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [confirmId, setConfirmId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('All')
  const [gender, setGender] = useState('All')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  const fetchCustomers = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (status !== 'All') params.set('status', status)
      if (gender !== 'All') params.set('gender', gender)
      params.set('page', String(page))
      params.set('limit', String(limit))
      const { data } = await api.get(`/admin/customers?${params}`)
      setCustomers(data.customers)
      setTotal(data.total)
    } finally {
      setLoading(false)
    }
  }, [search, status, gender, page, limit])

  useEffect(() => { void fetchCustomers() }, [fetchCustomers])
  useEffect(() => { setPage(1) }, [search, status, gender, limit])

  const handleDelete = async () => {
    if (!confirmId) return
    setDeletingId(confirmId)
    setConfirmId(null)
    try {
      await api.delete(`/admin/customers/${confirmId}`)
      setCustomers(prev => prev.filter(c => c.id !== confirmId))
      setTotal(prev => Math.max(prev - 1, 0))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <PageTitle title={`Customers (${total})`} subtitle="View, edit, or delete registered customers." />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input
            className="w-full rounded-lg border border-stone-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-mango-orange focus:ring-4 focus:ring-mango-yellow/25"
            placeholder="Search by name, mobile or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-mango-orange" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <select className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-mango-orange" value={gender} onChange={e => setGender(e.target.value)}>
          <option value="All">All Genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <section className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-16 text-center text-sm text-stone-400">Loading customers…</div>
          ) : customers.length === 0 ? (
            <div className="py-16 text-center text-sm text-stone-400">No customers found.</div>
          ) : (
            <table className="w-full min-w-[840px] text-left text-sm">
              <thead className="border-b border-stone-100 bg-stone-50 text-xs font-semibold uppercase tracking-wide text-stone-500">
                <tr>
                  <th className="px-4 py-3">Id</th>
                  <th className="px-4 py-3">Full Name</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Gender</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {customers.map(customer => (
                  <tr className="hover:bg-stone-50" key={customer.id}>
                    <td className="px-4 py-3 font-semibold text-stone-600">#{customer.id}</td>
                    <td className="px-4 py-3 font-semibold text-leaf-dark">{customer.name}</td>
                    <td className="px-4 py-3">{customer.mobile}</td>
                    <td className="px-4 py-3">{customer.email || '-'}</td>
                    <td className="px-4 py-3">{customer.gender}</td>
                    <td className="px-4 py-3">{formatDate(customer.joined)}</td>
                    <td className="px-4 py-3"><StatusPill value={customer.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button className="rounded-lg p-2 text-stone-500 hover:bg-stone-100" onClick={() => navigate(`/customers/${customer.id}`)} title="View" type="button">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="rounded-lg p-2 text-stone-500 hover:bg-stone-100" onClick={() => navigate(`/customers/${customer.id}/edit`)} title="Edit" type="button">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button className="rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:opacity-40" onClick={() => setConfirmId(customer.id)} disabled={deletingId === customer.id} title="Delete" type="button">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pagination
          limit={limit}
          onLimitChange={setLimit}
          onPageChange={setPage}
          page={page}
          total={total}
        />
      </section>

      {confirmId !== null && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-stone-950/40 p-4">
          <div className="w-full max-w-sm rounded-xl border border-stone-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-stone-950">Delete customer?</h3>
                <p className="mt-1 text-sm leading-6 text-stone-600">This action cannot be undone.</p>
              </div>
              <button className="rounded-lg p-1 text-stone-400 hover:bg-stone-100" onClick={() => setConfirmId(null)} type="button">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setConfirmId(null)} variant="ghost">Cancel</Button>
              <Button onClick={handleDelete} variant="danger">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
